import { Input } from 'antd';

import type { TemplateDetailVO } from '@/3.0/ai-viral-template/_components/template-ui/types';
import type { UseTranslateType } from '@/hooks/useTranslate';

import { GroupedImageUploadFieldRow, SingleImageUploadField } from './ImageUploadField';

/** 中性分割线 */
export const FIELD_BORDER = '#DCDCDC';
const TEXT_PRIMARY = '#111111';
const TEXT_COUNT = 'rgba(17,17,17,0.35)';

/** Figma：必填星号 */
export const RequiredMark = () => <span className="text-[red]">*</span>;

/** 单行 input */
export function TextInputField({
  label,
  required,
  placeholder,
  maxLength,
  value,
  onChange,
  t,
}: {
  label: string;
  required: boolean;
  placeholder: string;
  maxLength: number;
  value: string;
  onChange: (next: string) => void;
  t: UseTranslateType;
}) {
  const len = value.length;

  return (
    <div className="flex w-full flex-col gap-[8px]">
      <div className="flex h-[20px] items-center">
        <span className="whitespace-nowrap text-[14px] font-medium leading-[20px] text-[#111]">
          {t(label)}
          {required ? <RequiredMark /> : null}
        </span>
      </div>
      <div
        className="relative box-border h-[48px] rounded-[8px] border border-solid bg-white px-[8px] py-[4px]"
        style={{ borderColor: FIELD_BORDER }}
      >
        <Input
          variant="borderless"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t(placeholder)}
          maxLength={maxLength}
          className="!m-0 !h-[16px] !min-h-0 !p-0 !text-[14px] !leading-[16px] !text-[#111] shadow-none !placeholder:text-[10px] !placeholder:leading-[14px] !placeholder:text-[#ACACAC]"
          styles={{
            input: { borderRadius: '0', boxShadow: 'none', color: TEXT_PRIMARY },
          }}
        />
        <span
          className="pointer-events-none absolute bottom-[6px] right-[8px] whitespace-nowrap text-[12px] leading-[16px] tabular-nums"
          style={{ color: TEXT_COUNT }}
        >
          {len}/{maxLength}
        </span>
      </div>
    </div>
  );
}

/** 多行 textarea */
export function TextareaField({
  label,
  required,
  placeholder,
  maxLength,
  value,
  onChange,
  t,
}: {
  label: string;
  required: boolean;
  placeholder: string;
  maxLength: number;
  value: string;
  onChange: (next: string) => void;
  t: UseTranslateType;
}) {
  const len = value.length;

  return (
    <div className="flex w-full flex-col gap-[8px] not-italic">
      <div className="flex items-center">
        <span className="whitespace-nowrap text-[14px] font-medium leading-[20px] text-[#111]">
          {t(label)}
          {required ? <RequiredMark /> : null}
        </span>
      </div>
      <div
        className="relative box-border h-[100px] rounded-[8px] border border-solid px-[8px] py-[4px]"
        style={{ borderColor: FIELD_BORDER }}
      >
        <Input.TextArea
          variant="borderless"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t(placeholder)}
          maxLength={maxLength}
          autoSize={false}
          className="!mb-0 !min-h-[52px] !w-full !resize-none !border-0 !p-0 !pb-[34px] !text-[14px] !leading-[16px] !text-[#111] shadow-none placeholder:!text-[12px] placeholder:!leading-[16px] placeholder:!text-[rgba(17,17,17,0.35)]"
          style={{ boxShadow: 'none', color: TEXT_PRIMARY }}
          styles={{
            textarea: {
              boxShadow: 'none',
              color: TEXT_PRIMARY,
              padding: 0,
            },
          }}
        />
        <span
          className="pointer-events-none absolute bottom-[8px] right-[8px] whitespace-nowrap text-[12px] leading-[12px] tabular-nums"
          style={{ color: TEXT_COUNT }}
        >
          {len}/{maxLength}
        </span>
      </div>
    </div>
  );
}

export function TemplateDetailFieldRow({
  field,
  value,
  onFieldChange,
  t,
  hideLabel = false,
}: {
  field: TemplateDetailVO['formSchema']['fields'][number];
  value: unknown;
  onFieldChange: (key: string, next: unknown) => void;
  t: UseTranslateType;
  hideLabel?: boolean;
}) {
  switch (field.type) {
    case 'input':
      return (
        <TextInputField
          label={field.label}
          required={field.required}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          value={String(value ?? '')}
          onChange={(next) => onFieldChange(field.key, next)}
          t={t}
        />
      );
    case 'textarea':
      return (
        <TextareaField
          label={field.label}
          required={field.required}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          value={String(value ?? '')}
          onChange={(next) => onFieldChange(field.key, next)}
          t={t}
        />
      );
    case 'imageUpload':
      return (
        <SingleImageUploadField
          field={field}
          value={String(value ?? '')}
          onChange={(next) => onFieldChange(field.key, next)}
          t={t}
          hideLabel={hideLabel}
        />
      );
    case 'groupedImageUpload':
      return (
        <GroupedImageUploadFieldRow
          field={field}
          value={field.slots.map((_, i) =>
            String((Array.isArray(value) ? value[i] : undefined) ?? '').trim(),
          )}
          onChange={(next) => onFieldChange(field.key, next)}
          t={t}
          hideLabel={hideLabel}
        />
      );
    case 'imageDisplay':
      return (
        <>
          <span className="text-[14px] font-[500] text-[#111]">{t(field.label)}</span>
          <div className="flex flex-wrap gap-[8px]">
            {field.images.map((url, i) => (
              <div
                key={`${field.key}-img-${i}`}
                className="h-[80px] w-[80px] overflow-hidden rounded-[8px] border border-solid border-[#E5E5E5]"
              >
                <img src={url} alt="" className="size-full object-contain" />
              </div>
            ))}
          </div>
        </>
      );
    default:
      return null;
  }
}
